import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import PortalShell from "@/components/PortalShell";
import { Users, GraduationCap, FileText, AlertCircle, Calendar, Clock, MessageSquare, Plus, UserCheck, X, Mail, Phone } from "lucide-react";

const stats = [
  { key: "active_individuals", label: "Active Individuals", icon: Users, tone: "sky" },
  { key: "active_employees", label: "Active Employees", icon: UserCheck, tone: "emerald" },
  { key: "trainings_due", label: "Trainings Due", icon: GraduationCap, tone: "amber" },
  { key: "new_referrals", label: "New Referrals", icon: FileText, tone: "sky" },
  { key: "documents_missing", label: "Docs Missing", icon: AlertCircle, tone: "rose" },
  { key: "annual_reviews_due", label: "Annual Reviews", icon: Calendar, tone: "slate" },
  { key: "upcoming_expirations", label: "Upcoming Expirations", icon: Clock, tone: "amber" },
  { key: "contact_messages", label: "Contact Messages", icon: MessageSquare, tone: "sky" },
];

const toneMap = {
  sky: "bg-sky-50 text-sky-700",
  emerald: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-700",
  rose: "bg-rose-50 text-rose-700",
  slate: "bg-slate-100 text-slate-700",
};

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  const [emps, setEmps] = useState([]);
  const [refs, setRefs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [msgFilters, setMsgFilters] = useState({ status: "all", topic: "all" });
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "employee", password: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [statsRes, usersRes, refsRes, msgsRes] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/referrals"),
        api.get("/admin/contact-messages"),
      ]);
      setS(statsRes.data);
      setEmps(usersRes.data);
      setRefs(refsRes.data);
      setMsgs(msgsRes.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setForm({ email: "", name: "", role: "employee", password: "" });
      setShowCreate(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Account deactivated" : "Account activated");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <PortalShell variant="admin">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <span className="wc-eyebrow">Administrator</span>
          <h1 className="mt-3 font-outfit text-4xl md:text-5xl font-semibold tracking-tight text-slate-900">Operations Overview</h1>
          <p className="mt-3 text-slate-600 text-sm max-w-lg">Manage employees, families, referrals, and compliance across your organization.</p>
        </div>
        <button data-testid="admin-add-user-btn" onClick={() => setShowCreate((v) => !v)} className="wc-btn-primary">
          <Plus className="w-4 h-4"/> Invite user
        </button>
      </div>

      {/* Create user form */}
      {showCreate && (
        <form onSubmit={createUser} className="wc-card p-6 mt-6 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="create-user-form">
          <input required placeholder="Full name" data-testid="new-user-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"/>
          <input required type="email" placeholder="Email" data-testid="new-user-email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"/>
          <input required type="password" placeholder="Temporary password (min 8)" data-testid="new-user-password" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"/>
          <select data-testid="new-user-role" value={form.role} onChange={(e)=>setForm({...form,role:e.target.value})}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white">
            <option value="employee">Employee</option>
            <option value="family">Family</option>
            <option value="admin">Admin</option>
          </select>
          <button data-testid="submit-new-user-btn" disabled={busy} className="wc-btn-primary disabled:opacity-60">{busy ? "Creating…" : "Create"}</button>
        </form>
      )}

      {/* Stats */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map(({ key, label, icon: Icon, tone }) => (
          <div key={key} className="wc-card p-6" data-testid={`stat-${key}`}>
            <div className={`w-10 h-10 rounded-xl grid place-items-center ${toneMap[tone]}`}><Icon className="w-4 h-4"/></div>
            <div className="mt-4 font-outfit text-3xl font-semibold text-slate-900">{s ? s[key] ?? 0 : "—"}</div>
            <div className="text-xs text-slate-500 mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* Users table */}
      <div className="mt-10 wc-card p-6 md:p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-outfit text-xl font-semibold text-slate-900">Users</h2>
          <div className="text-xs text-slate-500">{emps.length} total</div>
        </div>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              <tr className="border-b border-slate-100">
                <th className="text-left py-3">Name</th>
                <th className="text-left py-3">Email</th>
                <th className="text-left py-3">Role</th>
                <th className="text-left py-3">Status</th>
                <th className="text-right py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {emps.map((u) => (
                <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="py-3 text-slate-600">{u.email}</td>
                  <td className="py-3"><span className="rounded-full bg-slate-100 text-slate-700 px-2.5 py-1 text-xs font-medium capitalize">{u.role}</span></td>
                  <td className="py-3">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${u.active ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.active ? "bg-emerald-500" : "bg-rose-500"}`}/>
                      {u.active ? "Active" : "Deactivated"}
                    </span>
                  </td>
                  <td className="py-3 text-right">
                    <button data-testid={`toggle-active-${u.id}`} onClick={() => toggleActive(u)} className="text-sky-700 hover:text-sky-800 text-xs font-semibold">
                      {u.active ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Referrals */}
      <div className="mt-10 wc-card p-6 md:p-8">
        <h2 className="font-outfit text-xl font-semibold text-slate-900">Recent Referrals</h2>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              <tr className="border-b border-slate-100">
                <th className="text-left py-3">Individual</th>
                <th className="text-left py-3">Referred by</th>
                <th className="text-left py-3">Contact</th>
                <th className="text-left py-3">Received</th>
              </tr>
            </thead>
            <tbody>
              {refs.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-500">No referrals yet.</td></tr>}
              {refs.map((r) => (
                <tr key={r.id} className="border-b border-slate-100" data-testid={`referral-row-${r.id}`}>
                  <td className="py-3 font-medium text-slate-900">{r.individual_name}</td>
                  <td className="py-3 text-slate-600">{r.referrer_name}<div className="text-xs text-slate-400">{r.relationship || "—"}</div></td>
                  <td className="py-3 text-slate-600">{r.referrer_email}<div className="text-xs text-slate-400">{r.referrer_phone || "—"}</div></td>
                  <td className="py-3 text-slate-500 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Contact Messages Inbox */}
      <div className="mt-10 wc-card p-6 md:p-8" data-testid="admin-messages-inbox">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-outfit text-xl font-semibold text-slate-900">Contact Inbox</h2>
            <div className="mt-0.5 text-xs text-slate-500">
              {msgs.length} total · {msgs.filter((m) => (m.status || "unread") === "unread").length} unread
            </div>
          </div>
        </div>

        {/* Filters */}
        {(() => {
          const uniqueTopics = Array.from(new Set(msgs.map((m) => m.topic).filter(Boolean)));
          const filtered = msgs.filter((m) => {
            const s = m.status || "unread";
            if (msgFilters.status !== "all" && s !== msgFilters.status) return false;
            if (msgFilters.topic !== "all" && (m.topic || "") !== msgFilters.topic) return false;
            return true;
          });
          const chipCls = (active) =>
            `px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest transition-colors ${
              active
                ? "bg-brand-navy text-white border border-brand-navy"
                : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"
            }`;
          return (
            <>
              <div className="mt-5 flex flex-wrap gap-4" data-testid="admin-inbox-filters">
                {/* Status filter */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">Status</span>
                  {[
                    { key: "all", label: "All" },
                    { key: "unread", label: "Unread" },
                    { key: "replied", label: "Replied" },
                  ].map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setMsgFilters((v) => ({ ...v, status: f.key }))}
                      className={chipCls(msgFilters.status === f.key)}
                      data-testid={`admin-inbox-filter-status-${f.key}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                {/* Topic filter */}
                {uniqueTopics.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">Topic</span>
                    <button
                      type="button"
                      onClick={() => setMsgFilters((v) => ({ ...v, topic: "all" }))}
                      className={chipCls(msgFilters.topic === "all")}
                      data-testid="admin-inbox-filter-topic-all"
                    >
                      All
                    </button>
                    {uniqueTopics.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setMsgFilters((v) => ({ ...v, topic: t }))}
                        className={chipCls(msgFilters.topic === t)}
                        data-testid={`admin-inbox-filter-topic-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6 space-y-3">
                {filtered.length === 0 && (
                  <div className="py-6 text-center text-sm text-slate-500" data-testid="admin-messages-empty">
                    {msgs.length === 0 ? "No contact messages yet." : "No messages match the current filters."}
                  </div>
                )}
                {filtered.map((m) => {
                  const status = m.status || "unread";
                  return (
                    <button
                      type="button"
                      key={m.id}
                      onClick={() => setSelectedMsg(m)}
                      className="w-full text-left rounded-2xl border border-slate-100 p-5 hover:border-brand-navy/30 hover:shadow-[0_10px_30px_rgba(30,42,120,0.06)]"
                      style={{ transition: "border-color 200ms ease, box-shadow 200ms ease" }}
                      data-testid={`admin-message-${m.id}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            {status === "unread" ? (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-orange/10 text-brand-orange px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest" data-testid={`admin-message-status-${m.id}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-brand-orange" /> Unread
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest" data-testid={`admin-message-status-${m.id}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Replied
                              </span>
                            )}
                            <span className="font-outfit font-semibold text-slate-900 text-[15px]">{m.name}</span>
                            {m.topic && (
                              <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest" data-testid={`admin-message-topic-${m.id}`}>
                                {m.topic}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
                            <span>{m.email}</span>
                            {m.phone && <span>{m.phone}</span>}
                          </div>
                        </div>
                        <span className="text-[11px] text-slate-400 shrink-0">{new Date(m.created_at).toLocaleString()}</span>
                      </div>
                      <p className="mt-3 text-sm text-slate-700 leading-relaxed line-clamp-2">{m.message}</p>
                    </button>
                  );
                })}
              </div>
            </>
          );
        })()}
      </div>

      {/* Message Detail Drawer */}
      {selectedMsg && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex justify-end"
          onClick={() => setSelectedMsg(null)}
          data-testid="admin-message-drawer-overlay"
        >
          <div
            className="w-full sm:max-w-lg h-full bg-white shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-message-drawer"
          >
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.24em] font-bold text-slate-500">Contact Message</div>
              <button
                type="button"
                onClick={() => setSelectedMsg(null)}
                className="w-9 h-9 rounded-full grid place-items-center text-slate-500 hover:bg-slate-100"
                data-testid="admin-message-drawer-close"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-6">
              <div className="flex flex-wrap items-center gap-2">
                {(() => {
                  const status = selectedMsg.status || "unread";
                  return status === "unread" ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-orange/10 text-brand-orange px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-orange" /> Unread
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Replied
                    </span>
                  );
                })()}
                <h3 className="font-outfit text-2xl font-semibold text-slate-900 tracking-tight">{selectedMsg.name}</h3>
                {selectedMsg.topic && (
                  <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">
                    {selectedMsg.topic}
                  </span>
                )}
              </div>
              <div className="mt-1 text-xs text-slate-400">
                Received {new Date(selectedMsg.created_at).toLocaleString()}
              </div>

              {/* Status toggle */}
              <div className="mt-5 flex items-center gap-2" data-testid="admin-message-status-actions">
                {(() => {
                  const status = selectedMsg.status || "unread";
                  const nextStatus = status === "unread" ? "replied" : "unread";
                  const label = status === "unread" ? "Mark as replied" : "Mark as unread";
                  const toggleStatus = async () => {
                    try {
                      await api.patch(`/admin/contact-messages/${selectedMsg.id}`, { status: nextStatus });
                      const updated = { ...selectedMsg, status: nextStatus };
                      setSelectedMsg(updated);
                      setMsgs((list) => list.map((x) => (x.id === selectedMsg.id ? updated : x)));
                      toast.success(nextStatus === "replied" ? "Marked as replied" : "Marked as unread");
                    } catch (e) { toast.error(formatApiError(e)); }
                  };
                  return (
                    <button
                      type="button"
                      onClick={toggleStatus}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-widest ${
                        status === "unread"
                          ? "bg-emerald-600 text-white hover:bg-emerald-700"
                          : "bg-white text-slate-700 border border-slate-200 hover:border-slate-300"
                      }`}
                      style={{ transition: "background-color 200ms ease, border-color 200ms ease" }}
                      data-testid="admin-message-toggle-status-btn"
                    >
                      {label}
                    </button>
                  );
                })()}
              </div>

              <div className="mt-6 space-y-3">
                <a
                  href={`mailto:${selectedMsg.email}?subject=${encodeURIComponent("Re: Your WeCare II inquiry")}&body=${encodeURIComponent(`Hi ${selectedMsg.name.split(" ")[0] || selectedMsg.name},\n\nThank you for reaching out to WeCare II.\n\n`)}`}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 hover:border-brand-navy"
                  style={{ transition: "border-color 200ms ease" }}
                  data-testid="admin-message-reply-email"
                >
                  <Mail className="w-4 h-4 text-brand-orange" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">Reply via email</div>
                    <div className="text-sm text-slate-800 font-medium truncate">{selectedMsg.email}</div>
                  </div>
                </a>
                {selectedMsg.phone && (
                  <a
                    href={`tel:${selectedMsg.phone}`}
                    className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 hover:border-brand-navy"
                    style={{ transition: "border-color 200ms ease" }}
                    data-testid="admin-message-call"
                  >
                    <Phone className="w-4 h-4 text-brand-orange" />
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">Call</div>
                      <div className="text-sm text-slate-800 font-medium">{selectedMsg.phone}</div>
                    </div>
                  </a>
                )}
              </div>

              <div className="mt-8">
                <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">Message</div>
                <p className="mt-3 text-[15px] text-slate-800 leading-relaxed whitespace-pre-line">{selectedMsg.message}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </PortalShell>
  );
}
